import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/config';
import { useAuth } from '../../context/AuthContext';
import {
    CheckCircle2,
    Circle,
    Plus,
    Trash2,
    Clock,
    AlertCircle,
    MoreVertical,
    ChevronRight,
    Calendar as CalendarIcon,
    Search,
    Filter,
    Edit3,
    X,
    Check,
    Save
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../context/AlertContext';
import PremiumDatePicker from '../../components/PremiumDatePicker';

const Tasks = () => {
    const { t } = useTranslation();
    const { user, userData } = useAuth();
    const { showAlert, showConfirm } = useAlert();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    // Dynamic collection name based on user role
    const getTaskCollection = () => {
        if (userData?.isSuperAdmin || userData?.is_super_admin) return 'super_admin_tasks';
        return 'system_tasks';
    };

    const [newTask, setNewTask] = useState({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        status: 'pending'
    });

    useEffect(() => {
        if (user?.uid && userData) {
            console.log("UserData loaded, fetching tasks. Admin status:", userData?.isSuperAdmin);
            fetchTasks();
        }
    }, [user?.uid, userData?.isSuperAdmin]);

    const fetchTasks = async () => {
        if (!user?.uid || !userData) {
            console.log("Waiting for user data before fetching...");
            return;
        }

        const currentCollection = getTaskCollection();
        setLoading(true);
        try {
            console.log(`Current Collection: ${currentCollection}, User UID: ${user.uid}`);

            const { data, error } = await supabase
                .from(currentCollection)
                .select('*')
                .eq('created_by', user.uid)
                .order('created_at', { ascending: false });

            if (error) throw error;

            const fetchedTasks = (data || []).map(r => ({
                ...r,
                createdBy: r.created_by,
                createdAt: r.created_at,
                updatedAt: r.updated_at,
                dueDate: r.due_date || r.dueDate
            }));

            console.log(`Successfully fetched ${fetchedTasks.length} tasks.`);
            setTasks(fetchedTasks);
        } catch (err) {
            console.error("SUPABASE ERROR:", err);
            showAlert("Görev çekme hatası: " + err.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const currentCollection = getTaskCollection();
        console.log("Saving task to collection:", currentCollection);

        try {
            const taskData = {
                title: newTask.title,
                description: newTask.description,
                priority: newTask.priority,
                due_date: newTask.dueDate,
                status: newTask.status,
                created_by: user.uid,
                updated_at: new Date().toISOString()
            };

            if (editingTask) {
                const { error } = await supabase
                    .from(currentCollection)
                    .update(taskData)
                    .eq('id', editingTask.id);
                if (error) throw error;
                console.log("Task updated successfully");
            } else {
                const { data, error } = await supabase
                    .from(currentCollection)
                    .insert({ ...taskData, created_at: new Date().toISOString() })
                    .select('id')
                    .single();
                if (error) throw error;
                console.log("Task added with ID:", data.id);
            }
            setIsModalOpen(false);
            setEditingTask(null);
            setNewTask({ title: '', description: '', priority: 'medium', dueDate: '', status: 'pending' });
            fetchTasks();
            showAlert("Görev başarıyla kaydedildi!", "success");
        } catch (err) {
            console.error("CRITICAL: Error saving task:", err);
            showAlert("Görev kaydedilemedi! Hata: " + err.message, "error");
        }
    };

    const toggleStatus = async (task) => {
        const currentCollection = getTaskCollection();
        const newStatus = task.status === 'completed' ? 'pending' : 'completed';
        try {
            const { error } = await supabase
                .from(currentCollection)
                .update({ status: newStatus, updated_at: new Date().toISOString() })
                .eq('id', task.id);
            if (error) throw error;
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
        } catch (err) {
            console.error("Error updating status:", err);
            showAlert("Durum güncellenemedi: " + err.message, "error");
        }
    };

    const handleDelete = async (id) => {
        if (!await showConfirm('Bu görevi silmek istediğinize emin misiniz?', { title: 'Görevi Sil', confirmText: 'Sil', type: 'warning' })) return;
        const currentCollection = getTaskCollection();
        try {
            const { error } = await supabase
                .from(currentCollection)
                .delete()
                .eq('id', id);
            if (error) throw error;
            setTasks(prev => prev.filter(t => t.id !== id));
        } catch (err) {
            console.error("Error deleting task:", err);
            showAlert("Görev silinemedi: " + err.message, "error");
        }
    };

    const filteredTasks = (tasks || []).filter(task => {
        const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStatus = filterStatus === 'all' || task.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return '#ef4444';
            case 'medium': return '#f59e0b';
            case 'low': return '#10b981';
            default: return '#64748b';
        }
    };

    return (
        <div style={{ padding: '0 1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div>
                    <h1 style={{ fontSize: '2.25rem', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>Görev Yönetimi</h1>
                    <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Platform büyüme ve operasyonel görevlerinizi takip edin.</p>
                </div>
                <button
                    onClick={() => { setEditingTask(null); setNewTask({ title: '', description: '', priority: 'medium', dueDate: '', status: 'pending' }); setIsModalOpen(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--primary)', color: 'white', padding: '12px 24px', borderRadius: '12px', fontWeight: 700, border: 'none', cursor: 'pointer', boxShadow: '0 4px 12px rgba(var(--primary-rgb), 0.3)' }}
                >
                    <Plus size={20} /> Görev Ekle
                </button>
            </div>

            <div className="card glass" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '300px', position: 'relative' }}>
                        <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} size={18} />
                        <input
                            type="text"
                            placeholder="Görevlerde ara..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ width: '100%', padding: '10px 10px 10px 40px', borderRadius: '10px', background: 'var(--bg-main)', border: '1px solid var(--border)', color: 'var(--text-main)' }}
                        />
                    </div>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{ padding: '10px', borderRadius: '10px', background: 'var(--bg-main)', border: '1px solid var(--border)', color: 'var(--text-main)', minWidth: '150px' }}
                    >
                        <option value="all">Tüm Durumlar</option>
                        <option value="pending">Bekleyenler</option>
                        <option value="completed">Tamamlananlar</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>Yükleniyor...</div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {filteredTasks.length === 0 ? (
                        <div className="card glass" style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
                            <CheckCircle2 size={48} style={{ marginBottom: '1rem', opacity: 0.2 }} />
                            <p>Henüz görev bulunmuyor.</p>
                        </div>
                    ) : (
                        filteredTasks.map(task => (
                            <div key={task.id} className="card glass transition-all" style={{ padding: '1.25rem', borderLeft: `4px solid ${getPriorityColor(task.priority)}`, display: 'flex', alignItems: 'center', gap: '1.5rem', opacity: task.status === 'completed' ? 0.7 : 1 }}>
                                <button
                                    onClick={() => toggleStatus(task)}
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: task.status === 'completed' ? '#10b981' : 'var(--text-muted)', display: 'flex', alignItems: 'center' }}
                                >
                                    {task.status === 'completed' ? <CheckCircle2 size={24} /> : <Circle size={24} />}
                                </button>

                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                                        <h3 style={{ fontSize: '1.125rem', fontWeight: 700, textDecoration: task.status === 'completed' ? 'line-through' : 'none', color: task.status === 'completed' ? 'var(--text-muted)' : 'var(--text-main)' }}>
                                            {task.title}
                                        </h3>
                                        <span style={{ fontSize: '0.625rem', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '4px', background: `${getPriorityColor(task.priority)}20`, color: getPriorityColor(task.priority), fontWeight: 800 }}>
                                            {task.priority === 'high' ? 'ACİL' : task.priority === 'medium' ? 'ORTA' : 'DÜŞÜK'}
                                        </span>
                                    </div>
                                    <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{task.description}</p>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: '2rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                                    {task.dueDate && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <CalendarIcon size={14} />
                                            {new Date(task.dueDate).toLocaleDateString()}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={() => { setEditingTask(task); setNewTask({ ...task }); setIsModalOpen(true); }} className="btn-icon" style={{ padding: '8px', color: 'var(--primary)' }}><Edit3 size={18} /></button>
                                        <button onClick={() => handleDelete(task.id)} className="btn-icon" style={{ padding: '8px', color: '#ef4444' }}><Trash2 size={18} /></button>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Task Form Modal */}
            {isModalOpen && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(8px)', padding: '1rem' }}
                    onClick={() => setIsModalOpen(false)}
                >
                    <div className="card glass task-modal-content" onClick={e => e.stopPropagation()} style={{ width: '500px', maxWidth: '100%', padding: '2rem', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                            <h2 style={{ fontSize: '1.5rem', fontWeight: 800 }}>{editingTask ? 'Görevi Düzenle' : 'Yeni Görev Ekle'}</h2>
                            <button onClick={() => setIsModalOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={24} /></button>
                        </div>

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div className="form-group">
                                <label>Görev Başlığı</label>
                                <input
                                    type="text"
                                    required
                                    value={newTask.title}
                                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                                    placeholder="Yapılacak işi kısaca tanımlayın..."
                                />
                            </div>

                            <div className="form-group">
                                <label>Açıklama (Opsiyonel)</label>
                                <textarea
                                    value={newTask.description}
                                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                                    placeholder="Görevle ilgili detaylar..."
                                    rows="3"
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }} className="task-form-grid">
                                <div className="form-group">
                                    <label>Öncelik</label>
                                    <select value={newTask.priority} onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })} style={{ width: '100%' }}>
                                        <option value="low">Düşük</option>
                                        <option value="medium">Orta</option>
                                        <option value="high">Yüksek / Acil</option>
                                    </select>
                                </div>
                                <PremiumDatePicker
                                    label="Son Tarih"
                                    value={newTask.dueDate}
                                    onChange={date => setNewTask({ ...newTask, dueDate: date })}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }} className="task-modal-buttons">
                                <button type="button" onClick={() => setIsModalOpen(false)} style={{ flex: 1, background: 'transparent', border: '1px solid var(--border)', padding: '12px', borderRadius: '12px' }}>İptal</button>
                                <button type="submit" className="btn-primary" style={{ flex: 2, fontWeight: 700, padding: '12px', borderRadius: '12px' }}>{editingTask ? 'Güncelle' : 'Görevi Kaydet'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <style>{`
                @media (max-width: 600px) {
                    .task-form-grid { grid-template-columns: 1fr !important; }
                    .task-modal-buttons { flex-direction: column !important; }
                    .task-modal-buttons button { width: 100% !important; }
                    .task-modal-content { padding: 1.5rem !important; }
                }
            `}</style>
        </div>
    );
};

export default Tasks;
